import {
  useCustomerServiceConfig,
  useMarketConfigs,
} from '../../features/settings';
import { formatDateTime } from '../../lib/format';
import type { CustomerServiceChannel } from '../../features/settings/types/settings.types';

const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50';
const providerNames = {
  legacy: '现有客服（脚本）',
  livechat: 'Text / LiveChat',
  custom: '其他客服链接',
};

function previewUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export function CustomerServicePage() {
  const markets = useMarketConfigs();
  const {
    data,
    form,
    selectedMarketId,
    loading,
    saving,
    error,
    dirty,
    updateField,
    selectMarket,
    save,
    reload,
  } = useCustomerServiceConfig();
  const selected = form.channels.find(
    (channel) => channel.id === form.channel_id,
  );
  const provider =
    selected?.provider ??
    (form.channel_id.startsWith('__unselected_')
      ? (form.channel_id.slice(13) as 'livechat' | 'custom')
      : 'legacy');
  const usedMarkets = (id: string) =>
    (data?.items ?? [])
      .filter(
        (item) => item.market_id !== selectedMarketId && item.channel_id === id,
      )
      .map((item) => item.market_id)
      .concat(form.channel_id === id ? [selectedMarketId] : []);
  const updateChannel = (id: string, patch: Partial<CustomerServiceChannel>) =>
    updateField(
      'channels',
      form.channels.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    );

  if (loading || markets.loading)
    return (
      <div className="py-20 text-center text-gray-500">正在加载客服配置…</div>
    );
  if (error || markets.error)
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error || markets.error}</p>
        <button
          onClick={() => {
            void reload();
            void markets.reload();
          }}
          className="text-sm text-primary"
        >
          重试
        </button>
      </div>
    );

  return (
    <div className="max-w-5xl space-y-6">
      <p className="text-sm text-gray-500">
        按地区选择供应商和客服渠道。保存后，App
        和官网下次打开客服时生效；正在进行的会话不会被切换。
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <fieldset
          disabled={saving}
          className="min-w-0 space-y-6 disabled:opacity-70"
        >
          <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
            <h2 className="font-medium text-gray-900">地区客服配置</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm text-gray-700">
                配置地区
                <select
                  aria-label="配置地区"
                  value={selectedMarketId}
                  onChange={(e) => selectMarket(e.target.value)}
                  className={inputCls}
                >
                  {markets.items.map((market) => (
                    <option key={market.market_id} value={market.market_id}>
                      {market.market_id} · {market.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => updateField('enabled', e.target.checked)}
                />
                启用本地区在线客服
              </label>
              <label className="space-y-1 text-sm text-gray-700">
                客服供应商
                <select
                  aria-label="客服供应商"
                  className={inputCls}
                  value={provider}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateField(
                      'channel_id',
                      value === 'legacy'
                        ? ''
                        : (form.channels.find(
                            (c) => c.provider === value && c.enabled,
                          )?.id ?? `__unselected_${value}`),
                    );
                  }}
                >
                  {Object.entries(providerNames).map(([value, name]) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              {provider !== 'legacy' && (
                <label className="space-y-1 text-sm text-gray-700">
                  客服渠道
                  <select
                    aria-label="客服渠道"
                    className={inputCls}
                    value={form.channel_id}
                    required
                    onChange={(e) => updateField('channel_id', e.target.value)}
                  >
                    <option value={`__unselected_${provider}`} disabled>
                      请选择下方已启用的客服渠道
                    </option>
                    {form.channels
                      .filter((c) => c.provider === provider)
                      .map((c) => (
                        <option key={c.id} value={c.id} disabled={!c.enabled}>
                          {c.name || '未命名渠道'}
                          {c.enabled ? '' : '（已停用）'}
                        </option>
                      ))}
                  </select>
                </label>
              )}
            </div>
            {selected && (
              <div className="text-sm break-all text-gray-500">
                {selected.mode === 'script'
                  ? '当前接入方式：供应商脚本'
                  : `当前客服链接：${selected.url}`}
                {selected.mode !== 'script' && previewUrl(selected.url) && (
                  <a
                    className="ml-3 text-primary"
                    href={previewUrl(selected.url)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                  >
                    预览客服
                  </a>
                )}
              </div>
            )}
            <details open={provider === 'legacy'}>
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                原客服脚本与旧版 App 兼容
              </summary>
              <p className="my-2 text-xs text-gray-500">
                保留原脚本供旧版 App
                使用；选择“现有客服”即可切回。没有原脚本的地区，旧版 App
                将显示客服暂不可用。仅填写可信的完整 script 内容。
              </p>
              <textarea
                aria-label="原客服脚本"
                value={form.script}
                onChange={(e) => updateField('script', e.target.value)}
                className={`${inputCls} min-h-[180px] font-mono text-xs`}
                spellCheck={false}
                placeholder="<script>...</script>"
              />
            </details>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-medium text-gray-900">客服渠道管理</h2>
              <button
                type="button"
                className="text-sm text-primary"
                disabled={form.channels.length >= 100}
                onClick={() =>
                  updateField('channels', [
                    ...form.channels,
                    {
                      id: `cs_${crypto.randomUUID()}`,
                      name: '',
                      provider: 'livechat',
                      mode: 'script',
                      script: '',
                      url: '',
                      enabled: true,
                    },
                  ])
                }
              >
                添加客服渠道
              </button>
            </div>
            <p className="text-xs text-gray-500">
              同一供应商可添加多个客服渠道。Text / LiveChat
              请粘贴供应商提供的完整安装脚本（含 script
              标签）。渠道修改会影响使用它的所有地区。
            </p>
            {form.channels.length === 0 && (
              <p className="py-4 text-center text-sm text-gray-400">
                尚未添加客服渠道，现有脚本客服仍可使用。
              </p>
            )}
            {form.channels.map((channel, index) => {
              const used = usedMarkets(channel.id);
              const url =
                channel.mode === 'script' ? null : previewUrl(channel.url);
              return (
                <fieldset
                  key={channel.id}
                  className="min-w-0 border-t border-gray-100 pt-4 space-y-3"
                >
                  <legend className="text-sm text-gray-500">
                    渠道 {index + 1}
                    {used.length > 0 ? ` · 使用地区：${used.join('、')}` : ''}
                  </legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-sm text-gray-700">
                      渠道名称
                      <input
                        aria-label={`渠道 ${index + 1} 名称`}
                        className={inputCls}
                        value={channel.name}
                        maxLength={100}
                        required
                        placeholder="例如：Text 越南客服"
                        onChange={(e) =>
                          updateChannel(channel.id, { name: e.target.value })
                        }
                      />
                    </label>
                    <label className="text-sm text-gray-700">
                      供应商
                      <select
                        aria-label={`渠道 ${index + 1} 供应商`}
                        className={inputCls}
                        value={channel.provider}
                        disabled={used.length > 0}
                        onChange={(e) =>
                          updateChannel(channel.id, {
                            provider: e.target
                              .value as CustomerServiceChannel['provider'],
                            mode:
                              e.target.value === 'livechat' ? 'script' : 'url',
                          })
                        }
                      >
                        <option value="livechat">Text / LiveChat</option>
                        <option value="custom">其他客服链接</option>
                      </select>
                    </label>
                  </div>
                  {channel.provider === 'livechat' && (
                    <label className="block text-sm text-gray-700">
                      接入方式
                      <select
                        aria-label={`渠道 ${index + 1} 接入方式`}
                        className={inputCls}
                        value={channel.mode || 'url'}
                        onChange={(e) =>
                          updateChannel(channel.id, {
                            mode: e.target.value as 'script' | 'url',
                          })
                        }
                      >
                        <option value="script">安装脚本（推荐）</option>
                        <option value="url">访客链接（兼容）</option>
                      </select>
                    </label>
                  )}
                  {channel.mode === 'script' ? (
                    <div className="space-y-2">
                      <label className="block text-sm text-gray-700">
                        客服安装脚本
                        <textarea
                          aria-label={`渠道 ${index + 1} 脚本`}
                          className={`${inputCls} min-h-[220px] font-mono text-xs`}
                          required
                          maxLength={65536}
                          spellCheck={false}
                          value={channel.script || ''}
                          placeholder={
                            '<script>\n  window.__lc = window.__lc || {};\n  window.__lc.license = 您的站点编号;\n  // 其余官方安装代码\n</script>'
                          }
                          onChange={(e) =>
                            updateChannel(channel.id, {
                              script: e.target.value,
                            })
                          }
                        />
                      </label>
                      <p className="text-xs text-gray-500">
                        直接粘贴完整 LiveChat 安装代码，无需填写 HTTP
                        链接。系统识别站点编号和客服组，通过官方组件加载；额外自定义
                        JavaScript 不会执行。保存后可在官网打开客服验证。
                      </p>
                    </div>
                  ) : (
                    <label className="block text-sm text-gray-700">
                      客服链接
                      <input
                        aria-label={`渠道 ${index + 1} 链接`}
                        className={inputCls}
                        type="url"
                        pattern="https://.*"
                        required
                        maxLength={4096}
                        placeholder="https://…"
                        value={channel.url}
                        onChange={(e) =>
                          updateChannel(channel.id, { url: e.target.value })
                        }
                      />
                    </label>
                  )}
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={channel.enabled}
                        disabled={used.length > 0}
                        onChange={(e) =>
                          updateChannel(channel.id, {
                            enabled: e.target.checked,
                          })
                        }
                      />
                      启用渠道
                    </label>
                    {url && (
                      <a
                        className="text-primary"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                      >
                        预览链接
                      </a>
                    )}
                    <button
                      type="button"
                      className="text-red-500 disabled:text-gray-400"
                      disabled={used.length > 0}
                      onClick={() => {
                        if (
                          window.confirm(
                            '删除这个未使用的客服渠道？保存后生效。',
                          )
                        )
                          updateField(
                            'channels',
                            form.channels.filter((c) => c.id !== channel.id),
                          );
                      }}
                    >
                      删除
                    </button>
                    {used.length > 0 && (
                      <span className="text-xs text-gray-400">
                        先解除地区选择，才能停用或删除。
                      </span>
                    )}
                  </div>
                </fieldset>
              );
            })}
          </section>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              disabled={saving || form.channel_id.startsWith('__unselected_')}
            >
              {saving ? '正在保存…' : '保存客服配置'}
            </button>
            {dirty && (
              <span className="text-xs text-amber-600">有未保存的修改</span>
            )}
          </div>
        </fieldset>
      </form>
      {data?.updated_at && (
        <p className="text-xs text-gray-400">
          最后更新：{formatDateTime(data.updated_at)}
        </p>
      )}
    </div>
  );
}
